import React, { useCallback, useEffect, useRef, useState } from "react";
import AgencyStep1Form from "./AgencyStep1Form";
import AgencyStep2Form from "./AgencyStep2Form";
import AgencyThankYou from "./AgencyThankYou";
import { generateAgencyQuotePDF } from "./AgencyPdfGenerator";
import { getMinEventDateValue, isDateBeforeMinEventDate } from "../dateUtils";
import { detectLanguage, getTranslations, locales } from "../i18n";
import "./agency.css";

const LOGO_URL = `${process.env.PUBLIC_URL || ""}/wedly-logo.png`;
const FALLBACK_PHONE_COUNTRIES = [
  { iso2: "IT", name: "Italia", dialCode: "+39", flag: "🇮🇹" },
  { iso2: "FR", name: "Francia", dialCode: "+33", flag: "🇫🇷" },
  { iso2: "DE", name: "Germania", dialCode: "+49", flag: "🇩🇪" },
  { iso2: "ES", name: "Spagna", dialCode: "+34", flag: "🇪🇸" },
  { iso2: "PT", name: "Portogallo", dialCode: "+351", flag: "🇵🇹" },
  { iso2: "CH", name: "Svizzera", dialCode: "+41", flag: "🇨🇭" },
  { iso2: "AT", name: "Austria", dialCode: "+43", flag: "🇦🇹" },
  { iso2: "BE", name: "Belgio", dialCode: "+32", flag: "🇧🇪" },
  { iso2: "NL", name: "Paesi Bassi", dialCode: "+31", flag: "🇳🇱" },
  { iso2: "LU", name: "Lussemburgo", dialCode: "+352", flag: "🇱🇺" },
  { iso2: "IE", name: "Irlanda", dialCode: "+353", flag: "🇮🇪" },
  { iso2: "GB", name: "Regno Unito", dialCode: "+44", flag: "🇬🇧" },
  { iso2: "US", name: "Stati Uniti", dialCode: "+1", flag: "🇺🇸" },
  { iso2: "CA", name: "Canada", dialCode: "+1", flag: "🇨🇦" },
  { iso2: "MX", name: "Messico", dialCode: "+52", flag: "🇲🇽" },
  { iso2: "BR", name: "Brasile", dialCode: "+55", flag: "🇧🇷" },
  { iso2: "AR", name: "Argentina", dialCode: "+54", flag: "🇦🇷" },
  { iso2: "CL", name: "Cile", dialCode: "+56", flag: "🇨🇱" },
  { iso2: "CO", name: "Colombia", dialCode: "+57", flag: "🇨🇴" },
  { iso2: "PE", name: "Perù", dialCode: "+51", flag: "🇵🇪" },
  { iso2: "VE", name: "Venezuela", dialCode: "+58", flag: "🇻🇪" },
  { iso2: "AU", name: "Australia", dialCode: "+61", flag: "🇦🇺" },
  { iso2: "NZ", name: "Nuova Zelanda", dialCode: "+64", flag: "🇳🇿" },
  { iso2: "JP", name: "Giappone", dialCode: "+81", flag: "🇯🇵" },
  { iso2: "CN", name: "Cina", dialCode: "+86", flag: "🇨🇳" },
  { iso2: "HK", name: "Hong Kong", dialCode: "+852", flag: "🇭🇰" },
  { iso2: "SG", name: "Singapore", dialCode: "+65", flag: "🇸🇬" },
  { iso2: "IN", name: "India", dialCode: "+91", flag: "🇮🇳" },
  { iso2: "PK", name: "Pakistan", dialCode: "+92", flag: "🇵🇰" },
  { iso2: "BD", name: "Bangladesh", dialCode: "+880", flag: "🇧🇩" },
  { iso2: "LK", name: "Sri Lanka", dialCode: "+94", flag: "🇱🇰" },
  { iso2: "AE", name: "Emirati Arabi Uniti", dialCode: "+971", flag: "🇦🇪" },
  { iso2: "SA", name: "Arabia Saudita", dialCode: "+966", flag: "🇸🇦" },
  { iso2: "QA", name: "Qatar", dialCode: "+974", flag: "🇶🇦" },
  { iso2: "KW", name: "Kuwait", dialCode: "+965", flag: "🇰🇼" },
  { iso2: "EG", name: "Egitto", dialCode: "+20", flag: "🇪🇬" },
  { iso2: "MA", name: "Marocco", dialCode: "+212", flag: "🇲🇦" },
  { iso2: "TN", name: "Tunisia", dialCode: "+216", flag: "🇹🇳" },
  { iso2: "DZ", name: "Algeria", dialCode: "+213", flag: "🇩🇿" },
  { iso2: "ZA", name: "Sudafrica", dialCode: "+27", flag: "🇿🇦" },
  { iso2: "NG", name: "Nigeria", dialCode: "+234", flag: "🇳🇬" },
  { iso2: "KE", name: "Kenya", dialCode: "+254", flag: "🇰🇪" },
  { iso2: "ET", name: "Etiopia", dialCode: "+251", flag: "🇪🇹" },
  { iso2: "TR", name: "Turchia", dialCode: "+90", flag: "🇹🇷" },
  { iso2: "GR", name: "Grecia", dialCode: "+30", flag: "🇬🇷" },
  { iso2: "CY", name: "Cipro", dialCode: "+357", flag: "🇨🇾" },
  { iso2: "RO", name: "Romania", dialCode: "+40", flag: "🇷🇴" },
  { iso2: "BG", name: "Bulgaria", dialCode: "+359", flag: "🇧🇬" },
  { iso2: "HR", name: "Croazia", dialCode: "+385", flag: "🇭🇷" },
  { iso2: "SI", name: "Slovenia", dialCode: "+386", flag: "🇸🇮" },
  { iso2: "SK", name: "Slovacchia", dialCode: "+421", flag: "🇸🇰" },
  { iso2: "CZ", name: "Repubblica Ceca", dialCode: "+420", flag: "🇨🇿" },
  { iso2: "HU", name: "Ungheria", dialCode: "+36", flag: "🇭🇺" },
  { iso2: "PL", name: "Polonia", dialCode: "+48", flag: "🇵🇱" },
  { iso2: "DK", name: "Danimarca", dialCode: "+45", flag: "🇩🇰" },
  { iso2: "SE", name: "Svezia", dialCode: "+46", flag: "🇸🇪" },
  { iso2: "NO", name: "Norvegia", dialCode: "+47", flag: "🇳🇴" },
  { iso2: "FI", name: "Finlandia", dialCode: "+358", flag: "🇫🇮" },
  { iso2: "IS", name: "Islanda", dialCode: "+354", flag: "🇮🇸" },
  { iso2: "LV", name: "Lettonia", dialCode: "+371", flag: "🇱🇻" },
  { iso2: "LT", name: "Lituania", dialCode: "+370", flag: "🇱🇹" },
  { iso2: "EE", name: "Estonia", dialCode: "+372", flag: "🇪🇪" },
  { iso2: "UA", name: "Ucraina", dialCode: "+380", flag: "🇺🇦" },
  { iso2: "RS", name: "Serbia", dialCode: "+381", flag: "🇷🇸" },
  { iso2: "AL", name: "Albania", dialCode: "+355", flag: "🇦🇱" },
  { iso2: "BA", name: "Bosnia ed Erzegovina", dialCode: "+387", flag: "🇧🇦" },
  { iso2: "MK", name: "Macedonia del Nord", dialCode: "+389", flag: "🇲🇰" },
  { iso2: "ME", name: "Montenegro", dialCode: "+382", flag: "🇲🇪" },
  { iso2: "MD", name: "Moldavia", dialCode: "+373", flag: "🇲🇩" },
  { iso2: "GE", name: "Georgia", dialCode: "+995", flag: "🇬🇪" },
  { iso2: "AM", name: "Armenia", dialCode: "+374", flag: "🇦🇲" },
  { iso2: "AZ", name: "Azerbaigian", dialCode: "+994", flag: "🇦🇿" },
  { iso2: "RU", name: "Russia", dialCode: "+7", flag: "🇷🇺" },
  { iso2: "KZ", name: "Kazakistan", dialCode: "+7", flag: "🇰🇿" },
];

export default function AgencyConfigurator() {
  const language = detectLanguage();
  const copy = getTranslations(language);
  const t = copy.b2c;
  const common = copy.common;
  const locale = locales[language] || locales.it;

  const [form, setForm] = useState({
    start_date: "",
    location: "",
    latitude: "",
    longitude: "",
    amount: "",
    amount_rain: "5mm",
    start_hour: "11:00",
    end_hour: "17:00",
    time_slot: "morning",
    first_name: "",
    last_name: "",
    email: "",
    phone_country_code: "+39",
    phone: "",
    fiscal_code: "",
    gender: "",
    birth_date: "",
    birth_country: "",
    residence: "",
    cap: "",
    notes: "",
    step1_privacy_seen: false,
    step1_marketing: false,
    step1_personalization: false,
    confirm_rain_protection: false,
    confirm_data_truth: false,
    confirm_privacy: false,
  });

  const [loading, setLoading] = useState(false);
  const [price, setPrice] = useState(null);
  const [step, setStep] = useState(1);
  const [pdfLibReady, setPdfLibReady] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [phoneCountries, setPhoneCountries] = useState(FALLBACK_PHONE_COUNTRIES);

  const locationRef = useRef(null);
  const birthCountryRef = useRef(null);
  const residenceRef = useRef(null);
  const capRef = useRef(null);

  const setField = useCallback((name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  }, []);

  const setTimeSlot = useCallback((slot) => {
    if (slot === "morning") {
      setForm((prev) => ({
        ...prev,
        time_slot: "morning",
        start_hour: "11:00",
        end_hour: "17:00",
      }));
      return;
    }

    setForm((prev) => ({
      ...prev,
      time_slot: "afternoon",
      start_hour: "18:00",
      end_hour: "24:00",
    }));
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadPhoneCountries = async () => {
      try {
        const response = await fetch("https://restcountries.com/v3.1/all?fields=cca2,name,idd,flag");
        if (!response.ok) return;

        const allCountries = await response.json();
        const parsed = allCountries
          .flatMap((country) => {
            const root = country?.idd?.root;
            const suffixes = country?.idd?.suffixes;
            if (!root || !Array.isArray(suffixes) || suffixes.length === 0) return [];

            return suffixes.map((suffix) => ({
              iso2: country.cca2 || "",
              name: country?.name?.common || country.cca2 || "",
              dialCode: `${root}${suffix}`.replace(/\s+/g, ""),
              flag: country.flag || "🏳️",
            }));
          })
          .filter((item) => item.iso2 && item.dialCode.startsWith("+"));

        const unique = [];
        const seen = new Set();
        parsed.forEach((item) => {
          const key = `${item.iso2}-${item.dialCode}`;
          if (seen.has(key)) return;
          seen.add(key);
          unique.push(item);
        });

        unique.sort((a, b) => a.name.localeCompare(b.name, "it"));

        if (!cancelled && unique.length > 0) {
          setPhoneCountries(unique);
        }
      } catch {
        // Keep the fallback list if the remote country list cannot be loaded.
      }
    };

    loadPhoneCountries();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const hasJsPDF = Boolean(window.jspdf);
    const hasAutoTable = Boolean(window.jspdf?.autoTable);

    const load = async () => {
      if (!hasJsPDF) {
        await new Promise((resolve, reject) => {
          const script = document.createElement("script");
          script.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
          script.onload = resolve;
          script.onerror = reject;
          document.body.appendChild(script);
        });
      }

      if (!hasAutoTable) {
        await new Promise((resolve, reject) => {
          const script = document.createElement("script");
          script.src =
            "https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.3/jspdf.plugin.autotable.min.js";
          script.onload = resolve;
          script.onerror = reject;
          document.body.appendChild(script);
        });
      }

      setPdfLibReady(true);
    };

    load().catch(() => setPdfLibReady(false));
  }, []);

  useEffect(() => {
    const waitForGoogle = setInterval(() => {
      if (!window.google?.maps?.places || !locationRef.current) return;

      clearInterval(waitForGoogle);

      const autocomplete = new window.google.maps.places.Autocomplete(locationRef.current, {
        componentRestrictions: { country: "it" },
        fields: ["formatted_address", "geometry"],
      });

      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        if (!place.geometry) return;

        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();

        setForm((prev) => ({
          ...prev,
          location: place.formatted_address,
          latitude: lat,
          longitude: lng,
        }));
      });
    }, 500);

    return () => clearInterval(waitForGoogle);
  }, []);

  useEffect(() => {
    if (step !== 2 || !window.google?.maps?.places) return;
    if (!birthCountryRef.current || !residenceRef.current || !capRef.current) return;

    const countryAutocomplete = new window.google.maps.places.Autocomplete(birthCountryRef.current, {
      types: ["(regions)"],
      fields: ["address_components", "formatted_address"],
    });

    const residenceAutocomplete = new window.google.maps.places.Autocomplete(residenceRef.current, {
      types: ["address"],
      componentRestrictions: { country: "it" },
      fields: ["formatted_address"],
    });

    const capAutocomplete = new window.google.maps.places.Autocomplete(capRef.current, {
      types: ["(regions)"],
      componentRestrictions: { country: "it" },
      fields: ["address_components", "formatted_address"],
    });

    countryAutocomplete.addListener("place_changed", () => {
      const place = countryAutocomplete.getPlace();
      const country =
        place.address_components?.find((component) => component.types.includes("country"))?.long_name ||
        place.formatted_address ||
        "";
      setField("birth_country", country);
    });

    residenceAutocomplete.addListener("place_changed", () => {
      const place = residenceAutocomplete.getPlace();
      setField("residence", place.formatted_address || "");
    });

    capAutocomplete.addListener("place_changed", () => {
      const place = capAutocomplete.getPlace();
      const postalCode =
        place.address_components?.find((component) => component.types.includes("postal_code"))?.long_name ||
        "";
      setField("cap", postalCode || place.formatted_address || "");
    });

    return () => {
      window.google?.maps?.event?.clearInstanceListeners(countryAutocomplete);
      window.google?.maps?.event?.clearInstanceListeners(residenceAutocomplete);
      window.google?.maps?.event?.clearInstanceListeners(capAutocomplete);
    };
  }, [setField, step]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.start_date || !form.location || !form.amount || !form.email || !form.step1_privacy_seen) {
      alert(common.requiredStep1);
      return;
    }

    if (isDateBeforeMinEventDate(form.start_date)) {
      alert(`${common.minDate} ${new Date(getMinEventDateValue()).toLocaleDateString(locale)}.`);
      return;
    }

    setLoading(true);
    setPrice(null);

    try {
      const response = await fetch("https://wedly.it/api/sendLead", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          caller: "getQuote",
          ...form,
        }),
      });

      const data = await response.json();

      if (data?.response?.price != null) {
        const calculatedPrice = data.response.price;
        setPrice(calculatedPrice);

        try {
          const emailResponse = await fetch("https://wedly.it/wp-json/api/sendConfirmationEmail", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              user_email: form.email,
              firstname: form.first_name || "Cliente",
              lastname: form.last_name || "",
              price: calculatedPrice,
              start_date: form.start_date,
              start_hour: form.start_hour,
              end_hour: form.end_hour,
              amount_rain: form.amount_rain,
              location: form.location,
              amount: form.amount,
              lang: language,
            }),
          });

          if (!emailResponse.ok) {
            throw new Error("B2C quote email failed");
          }
        } catch {
          alert(common.emailSummaryError);
        }
      } else {
        alert(common.quoteUnavailable);
      }
    } catch {
      alert(common.connectionError);
    } finally {
      setLoading(false);
    }
  };

  const handleFinalize = async (e) => {
    e.preventDefault();

    if (
      !form.first_name ||
      !form.last_name ||
      !form.email ||
      !form.phone ||
      !form.fiscal_code ||
      !form.gender ||
      !form.birth_date ||
      !form.birth_country ||
      !form.residence ||
      !form.cap ||
      !form.confirm_rain_protection ||
      !form.confirm_data_truth ||
      !form.confirm_privacy
    ) {
      alert(common.requiredFinalize);
      return;
    }

    setLoading(true);

    try {
      const selectedPrefix = (form.phone_country_code || "+39").trim();
      const normalizedPhone = (form.phone || "").trim();
      const phoneWithPrefix = normalizedPhone.startsWith("+")
        ? normalizedPhone
        : `${selectedPrefix} ${normalizedPhone}`;

      await fetch("https://wedly.it/wp-json/api/send_b2b_quote_email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: form.first_name,
          last_name: form.last_name,
          email: form.email,
          phone: phoneWithPrefix,
          phone_country_code: selectedPrefix,
          fiscal_code: form.fiscal_code,
          gender: form.gender,
          birth_date: form.birth_date,
          birth_country: form.birth_country,
          residence: form.residence,
          cap: form.cap,
          notes: form.notes,
          location: form.location,
          start_date: form.start_date,
          start_hour: form.start_hour,
          end_hour: form.end_hour,
          amount: form.amount,
          amount_rain: form.amount_rain,
          price,
          step1_privacy_seen: form.step1_privacy_seen ? common.yes : common.no,
          step1_marketing: form.step1_marketing ? common.yes : common.no,
          step1_personalization: form.step1_personalization ? common.yes : common.no,
          confirm_rain_protection: form.confirm_rain_protection ? common.yes : common.no,
          confirm_intent: form.confirm_rain_protection ? common.yes : common.no,
          confirm_data_truth: form.confirm_data_truth ? common.yes : common.no,
          confirm_privacy: form.confirm_privacy ? common.yes : common.no,
          lang: language,
        }),
      });

      setStep(3);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      alert(common.sendError);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    setPdfDownloading(true);
    try {
      await generateAgencyQuotePDF(form, price, LOGO_URL, "#1f3763", { language, locale, t, common });
    } finally {
      setPdfDownloading(false);
    }
  };

  const proceedToStep2 = () => {
    if (price == null) {
      alert(common.calculateFirst);
      return;
    }
    setStep(2);
  };

  const formatCurrency = (value) =>
    Number(value || 0).toLocaleString(locale, { style: "currency", currency: "EUR" });

  return (
    <div className="agency-configurator">
      <div className="agency-card">
        {step === 1 ? (
          <AgencyStep1Form
            form={form}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            loading={loading}
            price={price}
            proceedToStep2={proceedToStep2}
            handleDownloadPDF={handleDownloadPDF}
            pdfLibReady={pdfLibReady}
            pdfDownloading={pdfDownloading}
            formatCurrency={formatCurrency}
            setTimeSlot={setTimeSlot}
            locationRef={locationRef}
            t={t}
          />
        ) : step === 2 ? (
          <AgencyStep2Form
            form={form}
            handleChange={handleChange}
            handleFinalize={handleFinalize}
            loading={loading}
            phoneCountries={phoneCountries}
            birthCountryRef={birthCountryRef}
            residenceRef={residenceRef}
            capRef={capRef}
            t={t}
            common={common}
          />
        ) : (
          <AgencyThankYou form={form} price={price} t={t} locale={locale} />
        )}
      </div>
    </div>
  );
}
